package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * SH-270: what a person may do at a workplace, per feature: a [AccessLevel] for each. The one
 * registry is functions/featureAccess.json (the functions read it; firestore.rules name the same
 * ids); FeatureAccessAgreementTest keeps this enum, [BuiltInRole] and that file in step.
 *
 * What every member may do for themselves (their own shifts, availability, requests, clocking in)
 * is not a feature: it comes with any active engagement ([Capability.WORK_SHIFTS]). Billing,
 * deleting a workplace, the owner's cost rules and workplace groups stay the owner's.
 *
 * [financial]: the built-in managers' split: a (operational) manager edits every feature that is
 * not financial, a financial manager and the owner edit all of them.
 */
enum class Feature(val id: String, val financial: Boolean) {
    /** Rosters and shifts, deciding swaps and open shifts, removed shifts, shift and manager notes, the import. */
    ROSTER("roster", false),
    /** Shift and day templates, the weekly skeleton. */
    TEMPLATES("templates", false),
    /** The team's availability and availability requests. */
    AVAILABILITY("availability", false),
    /** Absences and leave, work requests, leave allowances. */
    REQUESTS("requests", false),
    /** The people of the workplace: profiles, engagements, invitations, staff notes. */
    TEAM("team", false),
    /** Job posts, applications, interviews. */
    HIRING("hiring", false),
    /** Courses, policies, health and safety records, readiness statuses. */
    TRAINING("training", false),
    /** Checklist templates and shift checklists. */
    CHECKLISTS("checklists", false),
    /** Announcements and who read them. */
    ANNOUNCEMENTS("announcements", false),
    /** Clock-in tablets (kiosk) and the clock review. */
    CLOCK("clock", false),
    /** Tip pools and shares. */
    TIPS("tips", false),
    /** The workplace's operational settings, places, local events, roster privacy. */
    SETTINGS("settings", false),
    /** Working-time reports and roster prints. */
    REPORTS("reports", false),
    /** Pay rates, contracts and contact details, HR dates, the employee timeline, documents, adjustments, payroll exports. */
    PAY("pay", true),
    /** Labour cost, costs, roster and shift financials, labour budget, financial settings. */
    FINANCES("finances", true),
    /** Daily takings and the till connection. */
    TAKINGS("takings", true);

    companion object {
        fun ofId(id: String): Feature? = entries.firstOrNull { it.id == id }
    }
}

/** SH-270: none, view (read-only), edit (includes create and delete). Stored as 0, 1, 2. */
enum class AccessLevel(val value: Int) {
    NONE(0),
    VIEW(1),
    EDIT(2);

    companion object {
        fun of(value: Int?): AccessLevel = entries.firstOrNull { it.value == value } ?: NONE
    }
}

/**
 * SH-270: the built-in roles, templates that give exactly what the single permission gave before
 * (functions/featureAccess.json builtInRoles). [permission] is the one an engagement of the role
 * keeps for older apps and the existing checks.
 */
enum class BuiltInRole(val id: String, val permission: UserPermission, val operational: AccessLevel, val financial: AccessLevel) {
    OWNER("owner", UserPermission.OWNER, AccessLevel.EDIT, AccessLevel.EDIT),
    MANAGER_FINANCIAL("manager_financial", UserPermission.MANAGER_FINANCIAL, AccessLevel.EDIT, AccessLevel.EDIT),
    MANAGER("manager", UserPermission.MANAGER, AccessLevel.EDIT, AccessLevel.NONE),
    EMPLOYEE("employee", UserPermission.EMPLOYEE, AccessLevel.NONE, AccessLevel.NONE);

    fun levelOf(feature: Feature): AccessLevel = if (feature.financial) financial else operational

    /** Every feature's level. */
    val levels: Map<Feature, AccessLevel> get() = Feature.entries.associateWith { levelOf(it) }

    companion object {
        fun ofId(id: String?): BuiltInRole? = entries.firstOrNull { it.id == id }
        fun of(permission: UserPermission): BuiltInRole = entries.first { it.permission == permission }
    }
}

/**
 * SH-270: users/{uid}.engagements[wp].access as functions/permissions.js writes it (the app never
 * writes it: the rules refuse that). [grants] are the effective levels keyed by the permission
 * they were computed with: while the engagement's permission is another one (changed by an older
 * app or a group grant, the function not caught up yet) they do not count and the permission's
 * built-in role does ([levelOf]), as in the rules.
 */
@Serializable
data class EngagementAccess(
    val roleId: String,
    /** A custom role's name (built-in roles are named by the app). */
    val roleName: String? = null,
    /** The person's modifications: only those that differ from the role. */
    val overrides: Map<Feature, AccessLevel> = emptyMap(),
    /** Permission name -> every feature's effective level. */
    val grants: Map<String, Map<Feature, AccessLevel>> = emptyMap()
) {
    val builtInRole: BuiltInRole? get() = BuiltInRole.ofId(roleId)
}

/**
 * [this] engagement's level for [feature], as firestore.rules levelOf and functions/permissions.js
 * levelOf read it: the grants for its stored permission, else the permission's built-in role.
 * Whether the engagement is active is the caller's business ([Engagement.capabilities]).
 */
fun Engagement.levelOf(feature: Feature): AccessLevel =
    access?.grants?.get(grantsKey)?.get(feature) ?: BuiltInRole.of(permission).levelOf(feature)

/**
 * The key of [EngagementAccess.grants] that counts: the stored permission's name while it is still
 * [Engagement.permission] (a copy with another permission, e.g. an optimistic edit, reads its
 * built-in role, as the rules would until the function recomputes the grants).
 */
private val Engagement.grantsKey: String
    get() = storedPermission?.takeIf { storedPermissionOf(it) == permission } ?: permission.name

/** A stored permission name as the app reads it (MANAGER_OPERATIONAL is MANAGER; unknown: employee). */
fun storedPermissionOf(name: String): UserPermission = when (name) {
    "MANAGER_OPERATIONAL" -> UserPermission.MANAGER
    else -> UserPermission.entries.firstOrNull { it.name == name } ?: UserPermission.EMPLOYEE
}

/** Whether [this] engagement is active and gives at least [level] of [feature]. */
fun Engagement.can(feature: Feature, level: AccessLevel = AccessLevel.VIEW): Boolean =
    isActive && levelOf(feature).value >= level.value

/** Every feature's level through [this] engagement (none once it ended). */
val Engagement.featureLevels: Map<Feature, AccessLevel>
    get() = Feature.entries.associateWith { if (isActive) levelOf(it) else AccessLevel.NONE }

/** [this] person's level for [feature] at [workplaceId]: none without an active engagement there. */
fun User.levelAt(workplaceId: String?, feature: Feature): AccessLevel =
    workplaceId?.let { engagements[it] }?.takeIf { it.isActive }?.levelOf(feature) ?: AccessLevel.NONE

/** Whether [this] person has at least [level] of [feature] at [workplaceId]. */
fun User.can(workplaceId: String?, feature: Feature, level: AccessLevel = AccessLevel.VIEW): Boolean =
    levelAt(workplaceId, feature).value >= level.value
